import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Cookie, Settings } from 'lucide-react';

const CookieBanner = ({ forceShow, onClose }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    essential: true,    // Always true, cannot be disabled
    analytics: false,   // Google Analytics, etc.
    marketing: false    // Marketing/Advertising cookies
  });

  useEffect(() => {
    // Check if user has already made cookie choices
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent || forceShow) {
      setShowBanner(true);
      if (forceShow) {
        setShowSettings(true); // Open settings directly when forced
      }
    } else {
      const settings = JSON.parse(cookieConsent);
      setCookieSettings(settings);
      // Initialize cookies based on saved settings
      initializeCookies(settings);
    }
  }, [forceShow]);

  const initializeCookies = (settings) => {
    // Here you would initialize your tracking scripts based on consent
    if (settings.analytics) {
      // Initialize Google Analytics or other analytics
      console.log('Analytics cookies enabled');
    }
    if (settings.marketing) {
      // Initialize marketing cookies
      console.log('Marketing cookies enabled');
    }
  };

  const acceptAll = () => {
    const newSettings = {
      essential: true,
      analytics: true,
      marketing: true
    };
    setCookieSettings(newSettings);
    localStorage.setItem('cookieConsent', JSON.stringify(newSettings));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    initializeCookies(newSettings);
    setShowBanner(false);
    setShowSettings(false);
    if (onClose) onClose(); // Notify parent component
  };

  const acceptSelected = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookieSettings));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    initializeCookies(cookieSettings);
    setShowBanner(false);
    setShowSettings(false);
    if (onClose) onClose(); // Notify parent component
  };

  const rejectAll = () => {
    const newSettings = {
      essential: true,
      analytics: false,
      marketing: false
    };
    setCookieSettings(newSettings);
    localStorage.setItem('cookieConsent', JSON.stringify(newSettings));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    initializeCookies(newSettings);
    setShowBanner(false);
    setShowSettings(false);
    if (onClose) onClose(); // Notify parent component
  };

  const updateCookieSetting = (type, value) => {
    setCookieSettings(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
      <Card className="w-full max-w-4xl shadow-2xl pointer-events-auto border-0">
        <CardContent className="p-6">
          {!showSettings ? (
            // Main Cookie Banner
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Cookie className="h-8 w-8 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Diese Website verwendet Cookies
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Wir verwenden Cookies, um Ihnen die beste Erfahrung auf unserer Website zu bieten. 
                    Einige Cookies sind für die Funktionalität der Website erforderlich, während andere uns helfen, 
                    die Website zu verbessern und Ihnen relevante Inhalte anzuzeigen.
                  </p>
                  <p className="text-gray-600 text-sm mt-2">
                    Mehr Informationen finden Sie in unserer{' '}
                    <button 
                      className="text-blue-600 hover:underline"
                      onClick={() => {
                        // Scroll to privacy policy
                        const element = document.querySelector('#privacy-policy');
                        if (element) element.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      Datenschutzerklärung
                    </button>.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  onClick={rejectAll}
                  variant="outline"
                  className="border-gray-400 text-gray-700 hover:bg-gray-50 font-semibold"
                >
                  Alle ablehnen
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="border-gray-400 text-gray-700 hover:bg-gray-50 font-semibold"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Einstellungen
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="border-gray-400 text-gray-700 hover:bg-gray-50 font-semibold"
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          ) : (
            // Cookie Settings
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">Cookie-Einstellungen</h3>
                <Button
                  onClick={() => setShowSettings(false)}
                  variant="ghost"
                  size="sm"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="space-y-4">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Essenzielle Cookies</h4>
                    <p className="text-sm text-gray-600">
                      Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end p-1">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Analyse-Cookies</h4>
                    <p className="text-sm text-gray-600">
                      Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, und ermöglichen es uns, die Website zu verbessern.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => updateCookieSetting('analytics', !cookieSettings.analytics)}
                      className={`w-12 h-6 rounded-full flex items-center p-1 transition-colors ${
                        cookieSettings.analytics ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">Marketing-Cookies</h4>
                    <p className="text-sm text-gray-600">
                      Diese Cookies werden verwendet, um Ihnen relevante Werbung und Inhalte zu zeigen und die Effektivität unserer Werbekampagnen zu messen.
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => updateCookieSetting('marketing', !cookieSettings.marketing)}
                      className={`w-12 h-6 rounded-full flex items-center p-1 transition-colors ${
                        cookieSettings.marketing ? 'bg-green-500 justify-end' : 'bg-gray-300 justify-start'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button
                  onClick={acceptSelected}
                  variant="outline"
                  className="border-gray-400 text-gray-700 hover:bg-gray-50 font-semibold"
                >
                  Auswahl speichern
                </Button>
                <Button
                  onClick={acceptAll}
                  variant="outline"
                  className="border-gray-400 text-gray-700 hover:bg-gray-50 font-semibold"
                >
                  Alle akzeptieren
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CookieBanner;